import React, { createContext, useContext, useState, useEffect } from 'react';

const FilterContext = createContext();

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState({
    dateRange: {
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
      endDate: new Date(),
      preset: 'last30days'
    },
    performance: 'all',
    channels: [],
    searchTerm: ''
  });

  // Date range presets
  const datePresets = {
    today: {
      label: 'วันนี้',
      startDate: new Date(),
      endDate: new Date()
    },
    yesterday: {
      label: 'เมื่อวาน',
      startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      endDate: new Date(new Date().setDate(new Date().getDate() - 1))
    },
    last7days: {
      label: '7 วันที่แล้ว',
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date()
    },
    last30days: {
      label: '30 วันที่แล้ว',
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date()
    },
    thisMonth: {
      label: 'เดือนนี้',
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date()
    },
    lastMonth: {
      label: 'เดือนที่แล้ว',
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0)
    },
    thisYear: {
      label: 'ปีนี้',
      startDate: new Date(new Date().getFullYear(), 0, 1),
      endDate: new Date()
    }
  };

  // Performance filters
  const performanceFilters = [
    { id: 'all', label: 'ทั้งหมด', description: 'สื่อโฆษณาทั้งหมด' },
    { id: 'excellent', label: 'ยอดเยี่ยม', description: 'คะแนน 81-100' },
    { id: 'good', label: 'ดี', description: 'คะแนน 61-80' },
    { id: 'average', label: 'ปานกลาง', description: 'คะแนน 41-60' },
    { id: 'poor', label: 'ต้องปรับปรุง', description: 'คะแนน 21-40' },
    { id: 'very-poor', label: 'ประสิทธิภาพต่ำมาก', description: 'คะแนน 0-20' },
    { id: 'high-roas', label: 'ROAS สูง', description: 'ROAS > 5x' },
    { id: 'low-cpa', label: 'CPA ต่ำ', description: 'CPA < 200' },
    { id: 'high-ctr', label: 'CTR สูง', description: 'CTR > 2%' }
  ];

  const updateDateRange = (preset, startDate = null, endDate = null) => {
    if (preset && datePresets[preset]) {
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...datePresets[preset],
          preset
        }
      }));
    } else if (startDate && endDate) {
      setFilters(prev => ({
        ...prev,
        dateRange: {
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          preset: 'custom'
        }
      }));
    }
  };

  const updatePerformanceFilter = (performance) => {
    setFilters(prev => ({
      ...prev,
      performance
    }));
  };

  const updateChannelFilter = (channels) => {
    setFilters(prev => ({
      ...prev,
      channels
    }));
  };

  const updateSearchTerm = (searchTerm) => {
    setFilters(prev => ({
      ...prev,
      searchTerm
    }));
  };

  const resetFilters = () => {
    setFilters({
      dateRange: {
        startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
        endDate: new Date(),
        preset: 'last30days'
      },
      performance: 'all',
      channels: [],
      searchTerm: ''
    });
  };

  // Helper function to check if media matches filters
  const matchesFilters = (media, calculateOverallScore, calculateAverageRoas, calculateAverageCpa, calculateAverageCtr) => {
    // Performance filter
    if (filters.performance !== 'all') {
      const overallScore = calculateOverallScore(media.channels);
      const avgRoas = calculateAverageRoas(media.channels);
      const avgCpa = calculateAverageCpa(media.channels);
      const avgCtr = calculateAverageCtr(media.channels);

      switch (filters.performance) {
        case 'excellent':
          if (overallScore < 81) return false;
          break;
        case 'good':
          if (overallScore < 61 || overallScore > 80) return false;
          break;
        case 'average':
          if (overallScore < 41 || overallScore > 60) return false;
          break;
        case 'poor':
          if (overallScore < 21 || overallScore > 40) return false;
          break;
        case 'very-poor':
          if (overallScore > 20) return false;
          break;
        case 'high-roas':
          if (avgRoas <= 5) return false;
          break;
        case 'low-cpa':
          if (avgCpa >= 200) return false;
          break;
        case 'high-ctr':
          if (avgCtr <= 2) return false;
          break;
      }
    }

    // Channel filter
    if (filters.channels.length > 0) {
      const mediaChannels = Object.keys(media.channels);
      const hasMatchingChannel = filters.channels.some(channel => mediaChannels.includes(channel));
      if (!hasMatchingChannel) return false;
    }

    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const nameMatch = media.name.toLowerCase().includes(searchLower);
      const productMatch = media.product.toLowerCase().includes(searchLower);
      if (!nameMatch && !productMatch) return false;
    }

    return true;
  };

  const value = {
    filters,
    datePresets,
    performanceFilters,
    updateDateRange,
    updatePerformanceFilter,
    updateChannelFilter,
    updateSearchTerm,
    resetFilters,
    matchesFilters
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};